<template>
  <div class="complete-button">
    <complete-button
      :is-complete="completeStatus"
      :complete-text="completeText"
      :disabled="disabled"
      @click-action="toggleComplete"
    />
  </div>
</template>

<script>
import { mapState, mapActions } from 'pinia'
import { useCalendarStore } from '@/stores/calendar'
import { useCompletesStore } from '@/stores/completes'
import { fbApi } from '@/utils/http-api'
import CompleteButton from '@/components/buttons/completes/complete-button.vue'

/**
 * Complete button for schedulable items that are NOT actually on the user's
 * schedule (yet).
 *
 * - stores in pinia calendar.unscheduledItem
 * - only for independent schedulable, not plans / routines / programs / challenges
 *
 * COMPLETE FLOW
 * - add to calendar AND complete
 * - pinia: show schedulable complete slider with trackers
 * - pinia: cannot trigger a program complete slider
 *
 * UNCOMPLETE FLOW
 * - completely remove from schedule
 * - pinia: no sliders
 */
export default {
  name: 'UnscheduledCompleteButton',
  components: { CompleteButton },
  props: {
    // schedulable type / id
    entityId: {
      type: Number,
      required: true,
    },
    entityType: {
      default: 'workout',
      type: String,
      validator: (val) =>
        ['plan', 'workout', 'custom_workout', 'article', 'wellness_video'].includes(val),
    },

    // only used for plan / article complete
    // will be empty string for video / custom workouts
    entityCategory: {
      default: '',
      validator: (val) => ['', 'program', 'challenge', 'routine', 'recipe'].includes(val),
    },

    // YYYY-MM-DD format string
    scheduleDate: {
      type: String,
      required: true,
    },

    // workouts { minutes, difficulty }
    // other trackers in the future
    trackerDefaults: {
      type: Object,
      default: () => ({}),
    },

    disabled: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['complete-updated'],
  data() {
    return {
      // make a mutable version of the isComplete prop
      loading: false,
      error: '',
    }
  },
  computed: {
    ...mapState(useCalendarStore, ['unscheduledItem']),

    // used for workout complete button on complete
    // passed down to modal component
    completeType() {
      // articles
      if (['article'].includes(this.entityType)) {
        // recipes
        if (['recipe'].includes(this.entityCategory)) {
          return 'Recipe'
        }

        return 'Article'
      }

      if (this.entityType === 'wellness_video') {
        return 'Video'
      }

      // workouts / programs / all others
      return 'Workout'
    },

    completeText() {
      return `${this.completeType} Complete`
    },

    // if processing, instantly flip status in UI
    // while XHR is processing, then revert to prop
    completeStatus() {
      let isCompleteNow = !!this.unscheduledItem?.isComplete

      if (this.loading) {
        return !isCompleteNow
      }

      return isCompleteNow
    },
  },

  methods: {
    ...mapActions(useCalendarStore, ['setUnscheduledItem', 'updateSlider', 'updateDayComplete']),
    ...mapActions(useCompletesStore, ['addComplete', 'removeComplete']),

    /**
     * If completing
     * - add to calendar
     * - mark complete
     * - show slide up modal
     *
     * If uncomplete
     * - remove from calender (and related data)
     */
    async toggleComplete() {
      // disable double submit
      if (this.loading) {
        return
      }

      // set loading and instantly toggle state (assuming API will be success)
      // for quick UI.  This will undo if error
      this.loading = true
      this.error = ''

      try {
        let res = null

        if (this.completeStatus && !this.unscheduledItem) {
          // add to calendar and complete
          res = await fbApi.post(`/calendar/schedule-complete`, {
            json: {
              scheduleDate: this.scheduleDate,
              schedulableType: this.entityType,
              schedulableId: this.entityId,
            },
          })

          // update pinia
          const { userScheduleItem, dayComplete } = res.body.data
          this.setUnscheduledItem(userScheduleItem)

          // push day complete status to pinia
          if (dayComplete) {
            this.updateDayComplete(dayComplete)
          }

          this.updateSlider({
            isOpen: true,
            type: 'schedulable',
            completeText: this.completeText,
            scheduledId: userScheduleItem.id,
            groupingId: null,
            entityId: this.entityId,
            entityType: this.entityType,
            entityCategory: this.entityCategory,
            plan: null,
            grouping: null,
            stats: null,
            trackerDefaults: this.trackerDefaults,
          })

          // increment pinia completes
          this.addComplete({
            schedulableId: this.entityId,
            schedulableType: this.entityType,
            completes: 1,
          })
        } else {
          // uncomplete and remove from schedule
          res = await fbApi.delete(`/calendar/schedule/${this.unscheduledItem.id}`)

          // reset pinia
          this.setUnscheduledItem(null)
          this.updateSlider({ isOpen: false, scheduledId: null })

          // decrement pinia completes
          this.removeComplete({
            schedulableId: this.entityId,
            schedulableType: this.entityType,
            completes: 1,
          })
        }

        // allow parent component to listen to new state
        this.$emit('complete-updated', this.completeStatus)
      } catch (e) {
        this.error = 'Error completing'
        console.error(e)
      } finally {
        // re-enable button action and let parent components know
        // complete is over
        this.loading = false
      }
    },
  },
}
</script>
